/**
 * pogi-atc-feedback.js — instant "Adding… → Added ✓" feedback on the add-to-cart CTA.
 *
 * Fills the ~1s gap between tap/click and the cart drawer opening. PURELY VISUAL:
 * it never preventDefaults, stops propagation, disables, or otherwise touches the
 * add-to-cart submit — it only swaps the [data-pogi-atc-label] text + a state class.
 *
 * Works on every [data-pogi-atc] button (PDP buy boxes + sticky bar), desktop AND
 * mobile, because they all bubble a `cart:update` event on a successful add
 * (product-form.js). Delegated on `document` so it also covers buttons that are
 * rendered or morphed in later.
 *
 * Cross-browser notes: ES5-only (var, function exprs, index for-loops — no NodeList
 * .forEach, no arrow fns). `click` fires on tap on all touch devices, so no separate
 * touch handling is needed (and avoids double-firing). Uses only widely-supported DOM
 * APIs: closest (guarded), classList, dataset, querySelectorAll, setAttribute.
 */
(function () {
  'use strict';

  var ADDING = 'Adding…'; // "Adding…"
  var ADDED = 'Added ✓';  // "Added ✓"
  var ADDED_MS = 1400; // how long "Added ✓" stays before reverting
  var FALLBACK_MS = 6000; // auto-revert if no cart:update arrives (e.g. failed add)

  // POGI 2026-07-23 (i18n wave): strings come from window.POGI_I18N (emitted by
  // theme.liquid via | t | json); the EN literals stay as fallbacks.
  function t(key, fallback) {
    var i18n = window.POGI_I18N;
    return (i18n && i18n[key]) || fallback;
  }

  function labelOf(btn) {
    return btn.querySelector('[data-pogi-atc-label]');
  }

  function toAdding(btn) {
    var l = labelOf(btn);
    if (l && typeof btn.getAttribute('data-pogi-orig') !== 'string') {
      btn.setAttribute('data-pogi-orig', l.textContent);
    }
    btn.classList.add('is-adding');
    btn.setAttribute('aria-busy', 'true');
    if (l) l.textContent = t('adding', ADDING);
    if (window.pogiAnnounce) window.pogiAnnounce(t('sr_adding', 'Adding to cart')); // SR cue (pogi-qol.js)
  }

  function toAdded(btn) {
    var l = labelOf(btn);
    btn.classList.remove('is-adding');
    btn.classList.add('is-added');
    btn.removeAttribute('aria-busy');
    if (l) l.textContent = t('added', ADDED);
  }

  function toReset(btn) {
    var l = labelOf(btn);
    var orig = btn.getAttribute('data-pogi-orig');
    btn.classList.remove('is-adding');
    btn.classList.remove('is-added');
    btn.removeAttribute('aria-busy');
    if (l && typeof orig === 'string') l.textContent = orig;
    btn.removeAttribute('data-pogi-orig');
  }

  // Tap/click on any add-to-cart CTA -> show "Adding…". Bubbling listener, NO
  // preventDefault/stopPropagation, so the native form submit proceeds untouched.
  document.addEventListener('click', function (e) {
    var node = e.target;
    if (node && node.nodeType === 3) node = node.parentNode; // text node -> element
    if (!node || typeof node.closest !== 'function') return;
    var btn = node.closest('[data-pogi-atc]');
    if (!btn) return;
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    if (/sold\s*out/i.test(btn.textContent || '')) return; // unavailable
    if (btn.classList.contains('is-adding')) return; // already in flight (double-tap)
    toAdding(btn);
    // Safety net: if the add never reports back (network/validation error), revert.
    window.setTimeout(function () {
      if (btn.classList.contains('is-adding')) toReset(btn);
    }, FALLBACK_MS);
  });

  // Add reported back (event bubbles to document) -> resolve any in-flight CTAs.
  // product-form.js fires cart:update with { didError: true } on FAILED/partial adds
  // too (sold-out, 422, exceed-max), so only show "Added ✓" on a genuine success;
  // otherwise just revert — never tell the customer it added when it didn't.
  document.addEventListener('cart:update', function (e) {
    var detail = e && e.detail && e.detail.data;
    var failed = !!(detail && detail.didError);
    var adding = document.querySelectorAll('[data-pogi-atc].is-adding');
    for (var i = 0; i < adding.length; i++) {
      (function (btn) {
        if (failed) {
          toReset(btn);
          return;
        }
        toAdded(btn);
        window.setTimeout(function () {
          toReset(btn);
        }, ADDED_MS);
      })(adding[i]);
    }
  });

  // Shared restore for navigate-away buttons (only used if nav/reload doesn't happen).
  function restoreBtn(btn) {
    if (!btn || btn.getAttribute('data-pogi-busy') !== '1') return;
    var o = btn.getAttribute('data-pogi-orig');
    if (typeof o === 'string') btn.innerHTML = o;
    btn.classList.remove('is-checkingout');
    btn.classList.remove('is-subscribing');
    btn.removeAttribute('aria-busy');
    btn.removeAttribute('data-pogi-busy');
    btn.removeAttribute('data-pogi-orig');
  }

  // --- Checkout buttons (cart page + drawer): they navigate away, so show a busy
  // "Checking out…" state on click + guard double-submit. NO preventDefault — the
  // native form submit (or pogi-cart #onSubmit /discount/ redirect) proceeds untouched.
  document.addEventListener('click', function (e) {
    var node = e.target;
    if (node && node.nodeType === 3) node = node.parentNode;
    if (!node || typeof node.closest !== 'function') return;
    var btn = node.closest('[data-pogi-checkout], .pogi-cartfoot__checkout');
    if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    if (btn.getAttribute('data-pogi-busy') === '1') return;
    btn.setAttribute('data-pogi-busy', '1');
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('is-checkingout');
    if (typeof btn.getAttribute('data-pogi-orig') !== 'string') btn.setAttribute('data-pogi-orig', btn.innerHTML);
    btn.innerHTML = t('checking_out', 'Checking out…');
    if (window.pogiAnnounce) window.pogiAnnounce(t('sr_checking_out', 'Checking out')); // SR cue (pogi-qol.js)
    window.setTimeout(function () { restoreBtn(btn); }, FALLBACK_MS); // rare no-nav fallback
  });

  // --- Email signup: native customer form -> full page reload. Show "Subscribing…"
  // on a valid submit (invalid required inputs never dispatch submit). Reload resets it.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.classList || !form.classList.contains('email-signup__form')) return;
    var btn = form.querySelector('.email-signup__button');
    if (!btn || btn.getAttribute('data-pogi-busy') === '1') return;
    btn.setAttribute('data-pogi-busy', '1');
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('is-subscribing');
    if (window.pogiAnnounce) window.pogiAnnounce(t('sr_subscribing', 'Subscribing')); // SR cue, both variants (pogi-qol.js)
    // Only swap the label on the TEXT variant. The icon (arrow) variant is a fixed
    // round button with aria-label="Sign up" — swapping in "Subscribing…" would
    // overflow it AND leave a stale accessible name, so keep the icon and let
    // aria-busy + the CSS cue convey the state.
    if (!btn.querySelector('svg')) {
      if (typeof btn.getAttribute('data-pogi-orig') !== 'string') btn.setAttribute('data-pogi-orig', btn.innerHTML);
      btn.innerHTML = t('subscribing', 'Subscribing…');
    }
    window.setTimeout(function () { restoreBtn(btn); }, FALLBACK_MS);
  }, true);

  // bfcache: a page restored from back/forward cache can carry a button frozen mid
  // "Checking out…"/"Subscribing…"/"Adding…". Reset on EVERY pageshow (harmless on a
  // fresh load — nothing is busy). Critical: otherwise the data-pogi-busy guard would
  // block re-clicking Checkout, leaving a dead checkout button after a Back navigation.
  window.addEventListener('pageshow', function () {
    var busy = document.querySelectorAll('[data-pogi-busy="1"]');
    for (var i = 0; i < busy.length; i++) restoreBtn(busy[i]);
    var adding = document.querySelectorAll('[data-pogi-atc].is-adding');
    for (var j = 0; j < adding.length; j++) toReset(adding[j]);
  });
})();
